import { $getSelection, $isRangeSelection, $isTextNode, ElementNode } from 'lexical';

import { CustomParagraphNode } from '~components/generic/editable-field/components/nodes/CustomParagraphNode';

// Define a type that includes the __className property
interface NodeWithClassName extends ElementNode {
  __className?: string;
}

export const handleStyleCommand = (editor, className) => {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      selection.getNodes().forEach(node => {
        // Handle edit mode styling
        if ($isTextNode(node)) {
          const domNode = editor.getElementByKey(node.getKey());
          let parent = domNode?.parentElement;

          // Check if parent is a heading, list, or list item
          if (
            parent &&
            (/^h[1-6]$/i.test(parent.tagName) || /^[uo]l$/i.test(parent.tagName) || /^li$/i.test(parent.tagName))
          ) {
            // Instead of replacing with a paragraph, just toggle the class on the DOM element
            if (parent.classList.contains(className)) {
              parent.classList.remove(className);
            } else {
              parent.classList.add(className);
            }

            // Also update the node's parent className if possible
            const parentNode = node.getParent();
            if (parentNode) {
              // Cast to our interface that includes __className
              const typedParent = parentNode as unknown as NodeWithClassName;

              if (typedParent.__className !== undefined) {
                const writableParent = parentNode.getWritable() as NodeWithClassName;
                const currentClasses = writableParent.__className || '';

                if (currentClasses.includes(className)) {
                  // Remove the class
                  const cleanedClasses = currentClasses
                    .split(' ')
                    .filter(cls => cls !== className)
                    .join(' ');
                  writableParent.__className = cleanedClasses;
                } else {
                  // Add the class
                  writableParent.__className = `${currentClasses} ${className}`.trim();
                }
              }
            }
            return; // Skip the rest of the logic for this node
          }

          if (parent) {
            // Toggle only the specific class that was requested
            if (parent.classList.contains(className)) {
              parent.classList.remove(className);
            } else {
              parent.classList.add(className);
            }
          }
        }

        // Handle view mode persistence
        const parent = node.getParent();

        if (parent && parent.getType() === 'custom-paragraph') {
          const customParagraph = parent as CustomParagraphNode;
          const writableParent = customParagraph.getWritable();

          const currentClasses = (writableParent as CustomParagraphNode).__customClassName || '';

          // Only toggle the specific class that was requested
          if (currentClasses.includes(className)) {
            // Remove the specific class
            const cleanedClasses = currentClasses
              .split(' ')
              .filter(cls => cls !== className)
              .join(' ');
            (writableParent as CustomParagraphNode).__customClassName = cleanedClasses;
          } else {
            // Keep current classes and add new className
            (writableParent as CustomParagraphNode).__customClassName = `${currentClasses} ${className}`.trim();
          }
        } else if (parent) {
          // Cast to our interface that includes __className
          const typedParent = parent as unknown as NodeWithClassName;

          if (typedParent.__className !== undefined) {
            // Handle other node types with className support
            const writableParent = parent.getWritable() as NodeWithClassName;
            const currentClasses = writableParent.__className || '';

            if (currentClasses.includes(className)) {
              // Remove the class
              const cleanedClasses = currentClasses
                .split(' ')
                .filter(cls => cls !== className)
                .join(' ');
              writableParent.__className = cleanedClasses;
            } else {
              // Add the class
              writableParent.__className = `${currentClasses} ${className}`.trim();
            }
          }
        }
      });
    }
  });
  return true;
};
